/**
 * =============================================================================
 * InvisibilityPipeline — cloak tipo "Predator" (refracción + shimmer que exhibe)
 * =============================================================================
 *
 * Render pipeline custom (SinglePipeline) que se aplica a CADA sprite de una
 * entidad invisible (`sprite.setPipeline(INVIS_PIPELINE_KEY)`). Samplea la textura
 * del personaje y la vuelve casi invisible, con:
 *   - una refracción/heat-haze continua (desplazamiento de UV) sobre la silueta,
 *   - un PULSO periódico (cada ~2-5s) que "exhibe" el cuerpo — sube el alpha y
 *     agrega un rim cyan en el contorno (el efecto Predator).
 *
 * El alpha base (cuánto se ve segun la relación con el observador: yo / party /
 * ver-invisibles / enemigo) lo pone el controller vía `sprite.setAlpha(...)`; el
 * shader lo lee de `outTint.a` y lo modula con el pulso. La FASE del pulso depende
 * de la posición de MUNDO del fragmento (uWorldView + gl_FragCoord, como el
 * WaterPipeline), así distintos personajes "aparecen" en momentos distintos y todo
 * batchea en pocos draw calls.
 *
 * Uniforms por frame (`uTime`, `uWorldView`) los setea GameScene.update() vía
 * `setFrame()`. `uResolution`/`uMainSampler` los inicializa SinglePipeline.
 * Ver docs/INVISIBILITY.md.
 */

import Phaser from 'phaser';

/** Key con la que se registra el pipeline en el PipelineManager. */
export const INVIS_PIPELINE_KEY = 'Invisibility';

const FRAG_SHADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform sampler2D uMainSampler;
uniform vec2 uResolution;
uniform vec4 uWorldView; // x, y, width, height del worldView de la cámara
uniform float uTime;

varying vec2 outTexCoord;
varying float outTintEffect;
varying vec4 outTint;

// ============================ Tuning ============================
const float PULSE_SPEED   = 1.7;   // velocidad del pulso (período ~ 2*pi/velocidad)
const float PULSE_LOW     = 0.55;  // umbral inferior del smoothstep del pulso
const float PULSE_HIGH    = 0.99;  // umbral superior (más alto = pulso más breve)
const float HAZE_BASE     = 0.006; // desplazamiento de UV en reposo (heat-haze sutil)
const float HAZE_PULSE    = 0.020; // desplazamiento extra durante el pulso
const float EXHIBIT_BOOST  = 2.4;  // cuánto sube el alpha durante el pulso
const float ALPHA_CEIL    = 0.92;  // techo de alpha (nunca 100% opaco: queda etéreo)
const vec3  RIM_COLOR     = vec3(0.55, 0.85, 1.0); // cyan espectral del contorno
// ================================================================

void main () {
  // Posición de mundo del fragmento → fase del pulso por-entidad (como WaterPipeline).
  vec2 frac = vec2(gl_FragCoord.x / uResolution.x, 1.0 - gl_FragCoord.y / uResolution.y);
  vec2 world = uWorldView.xy + frac * uWorldView.zw;
  float phase = dot(floor(world / 32.0), vec2(0.7, 1.3));

  // Pulso Predator: bajo casi siempre, spike periódico, desfasado por posición.
  float wave = sin(uTime * PULSE_SPEED + phase);
  float pulse = smoothstep(PULSE_LOW, PULSE_HIGH, wave);

  // Refracción/heat-haze: desplazamiento de UV con ondas (más fuerte en el pulso).
  float amp = HAZE_BASE + HAZE_PULSE * pulse;
  vec2 uv = outTexCoord;
  uv.x += sin(outTexCoord.y * 42.0 + uTime * 6.0 + phase) * amp;
  uv.y += cos(outTexCoord.x * 42.0 + uTime * 5.0 + phase) * amp;

  vec4 tex = texture2D(uMainSampler, uv);
  if (tex.a < 0.02) discard;

  // Rim: gradiente de alpha (borde de la silueta) → brillo que resalta el contorno.
  float px = 1.5 / 256.0;
  float aL = texture2D(uMainSampler, uv - vec2(px, 0.0)).a;
  float aR = texture2D(uMainSampler, uv + vec2(px, 0.0)).a;
  float aU = texture2D(uMainSampler, uv - vec2(0.0, px)).a;
  float aD = texture2D(uMainSampler, uv + vec2(0.0, px)).a;
  float edge = clamp(abs(aL - aR) + abs(aU - aD), 0.0, 1.0);

  // Alpha base = alpha del sprite (relación con el observador) * alpha de la textura.
  float baseA = tex.a * outTint.a;
  // Exhibir el cuerpo durante el pulso: sube alpha (con techo etéreo).
  float a = min(baseA * (1.0 + pulse * EXHIBIT_BOOST), tex.a * ALPHA_CEIL);

  // Color: textura + rim cyan (contorno) reforzado en el pulso.
  vec3 col = tex.rgb + RIM_COLOR * edge * (0.20 + 0.9 * pulse) + RIM_COLOR * pulse * 0.12;

  gl_FragColor = vec4(col, a);
}
`;

/** Cloak de invisibilidad (refracción + shimmer que exhibe el cuerpo). */
export class InvisibilityPipeline extends Phaser.Renderer.WebGL.Pipelines.SinglePipeline {
  constructor(game: Phaser.Game) {
    super({ game, fragShader: FRAG_SHADER });
  }

  /**
   * Actualiza los uniforms por frame. Llamar desde GameScene.update() con el tiempo
   * (segundos) y el worldView de la cámara principal (ancla la fase del pulso al mundo).
   */
  setFrame(timeSeconds: number, worldView: Phaser.Geom.Rectangle): void {
    this.set1f('uTime', timeSeconds);
    this.set4f('uWorldView', worldView.x, worldView.y, worldView.width, worldView.height);
  }
}
